import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { getAsset } from '../../../../../../util/assets';
import { identitiesFilteredOnAccess } from '../../../../../../util/identity';
import { isAlbum } from '../../../../../../util/album';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../../../../constants/album-folder';
import { ALBUM_IDENTITY_ACCESS } from '../../../../../../constants/identity';
import useAutoJoinNullState from './hooks/useAutoJoinNullState';
import PrepaidPrintPackagesPurchaseContent from './components/PrepaidPrintPackagesPurchaseContent';
import PrepaidItemsPurchaseModal, {
  PREPAID_ITEMS_PURCHASE_MODAL,
} from '../../../../../../components/global/PrepaidItemsPurchaseModal';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../hooks/useCloseModal';
import { CHECKOUT_CART_MODAL } from '../../../../../../services/checkout/components/CheckoutCart/modal';

const propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      hierarchyItemId: PropTypes.string,
      albumMembershipGroupId: PropTypes.string,
    }),
  }),
  entity: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
};

const AutoJoinNullState = ({ match, entity, className }) => {
  const {
    user,
    loadingUser,
    albumMembershipGroup,
    loadingAlbumMembershipGroup,
    albumFolderJoin,
    albumsJoinByCode,
    hasPrepaidPrintPackages,
    activePromoCode,
  } = useAutoJoinNullState({
    rootAlbumFolderId: isAlbum(entity)
      ? entity.root_album_folder?.id
      : entity?.id,
    hierarchyItemId:
      match?.params?.hierarchyItemId ?? match?.params?.albumMembershipGroupId,
  });
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const classes = classNames({
    'd-flex flex-column text-center align-items-center justify-content-center px-2': true,
    [className]: !!className,
  });

  const onJoin = () => {
    const albumFolder = isAlbum(entity) ? entity.root_album_folder : entity;

    if (!albumFolder?.joins_enabled || albumFolder.matching_disabled) {
      return;
    }

    const [primaryIdentity] = identitiesFilteredOnAccess(
      user.linked_identities?.records ?? [],
      ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY,
    );

    if (primaryIdentity) {
      if (albumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL) {
        albumFolderJoin({
          album_folder_id: albumFolder?.id,
          identity_ids: [primaryIdentity.id],
        });
      }

      // This will not really support galleries with sub-folders.
      // The ideal scenario is for Join type to always be join-all.
      // This is mainly a safeguard for situations which we can do something about.
      if (albumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.INDIVIDUAL) {
        // Attempt to get 1 child.

        const album = isAlbum(entity)
          ? entity
          : albumFolder.children?.albums?.records?.[0] ?? null;

        if (!album) {
          return;
        }

        albumsJoinByCode({
          account_id: user?.id,
          identities: [primaryIdentity.id],
          join_codes: [album.join_code],
        });
      }
    }
  };

  useEffect(() => {
    if (
      user &&
      !albumMembershipGroup &&
      !loadingAlbumMembershipGroup &&
      !loadingUser
    ) {
      onJoin();
    }
  }, [user, loadingUser, loadingAlbumMembershipGroup, albumMembershipGroup]);

  return (
    <div className={classes}>
      <img
        src={getAsset('/waldo-icon.svg')}
        className="grayscale-faded mb-3"
        width="40"
        alt="Waldo Icon"
      />
      <h3>No photos have been uploaded to this event yet.</h3>
      <p>We&apos;ll send you a text when photos are added to this event!</p>
      {hasPrepaidPrintPackages && (
        <PrepaidPrintPackagesPurchaseContent
          activePromoCode={activePromoCode}
          onPurchase={() => openModal(PREPAID_ITEMS_PURCHASE_MODAL)}
        />
      )}
      <PrepaidItemsPurchaseModal
        albumFolderId={
          isAlbum(entity) ? entity.root_album_folder?.id : entity?.id
        }
        onCheckout={() => {
          closeModal(PREPAID_ITEMS_PURCHASE_MODAL);
          openModal(CHECKOUT_CART_MODAL, {
            stripeModalProps: {
              defaultPromoCode: activePromoCode?.code ?? '',
              description: (
                <Fragment>
                  <p className="text-center">Checkout</p>
                  {activePromoCode && (
                    <p className="text-extra-bold text-center">
                      Promo code: {activePromoCode.code}
                    </p>
                  )}
                </Fragment>
              ),
            },
          });
        }}
      />
    </div>
  );
};

AutoJoinNullState.propTypes = propTypes;

export { AutoJoinNullState as AutoJoinNullStateComponent };

export default compose(withRouter)(AutoJoinNullState);
